// @flow

import { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';
import isEmpty from 'lodash/isEmpty';

import ComplianceApprovalService from '../../services/complianceApprovalService';

// One page of certs in a single stage, for one property. Paginated from day one
// (a real property can hold hundreds of open certs in one stage), so this is an
// infinite query: the first page loads with the panel, and "load more" appends
// the next page onto the same cache entry without reloading the screen.
//
// The endpoint is tenant-scoped under `/{organizationId}/{propertyId}/`. The
// query key carries the organization, property, and stage, so selecting a
// different stage (a funnel click) or a different property fetches the right
// list and react-query caches each separately. It is disabled until all three
// are known.
//
// useErrorBoundary is deliberately false: a failure surfaces as the panel's own
// inline error state (via AsyncBox), which leaves the funnel beside it usable
// rather than blanking the page through an error boundary.
export const DEFAULT_PAGE_SIZE = 25;

const useComplianceApprovalCertsInStageQuery = ({
  organizationId,
  propertyId,
  stageKey,
  pageSize = DEFAULT_PAGE_SIZE,
  options = {},
}: {
  organizationId: ?string,
  propertyId: ?string,
  stageKey: ?string,
  pageSize?: number,
  options?: Object,
}) => {
  const complianceApprovalService = useMemo(
    () => new ComplianceApprovalService(),
    [],
  );

  return useInfiniteQuery(
    ['compliance-approval-certs', organizationId, propertyId, stageKey],
    ({ pageParam = 1 }) =>
      complianceApprovalService.getCertsInStage({
        organizationId: organizationId ?? '',
        propertyId: propertyId ?? '',
        stageKey: stageKey ?? '',
        page: pageParam,
        pageSize,
      }),
    {
      // The response reports whether another page exists and which page it just
      // returned; the next page is simply the one after it. When hasMore is
      // false, returning undefined tells react-query there is nothing more to
      // fetch (hasNextPage becomes false).
      getNextPageParam: (lastPage: Object) =>
        lastPage?.hasMore ? (lastPage.page ?? 1) + 1 : undefined,
      refetchOnWindowFocus: false,
      useErrorBoundary: false,
      enabled:
        !isEmpty(organizationId ?? '') &&
        !isEmpty(propertyId ?? '') &&
        !isEmpty(stageKey ?? ''),
      ...options,
    },
  );
};

export default useComplianceApprovalCertsInStageQuery;
